import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/modules/auth'
import type { User, LoginRequest, RegisterRequest, ChangePasswordRequest } from '@/interface/auth'
import { getPostLoginRedirectUrl } from '@/utils/redirect'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const isUserLoading = ref(false)

  // Initialize from localStorage (only token)
  const initAuth = () => {
    const storedToken = localStorage.getItem('clipflow_token')

    if (storedToken) {
      token.value = storedToken
    }
  }

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  const authToken = computed(() => token.value)

  // Actions
  const login = async (email: string, password: string) => {
    isLoading.value = true
    try {
      const loginData: LoginRequest = { email, password }
      const response = await authApi.login(loginData)

      // Store token immediately
      token.value = response.access_token
      localStorage.setItem('clipflow_token', response.access_token)

      // Get user info but don't store in localStorage
      const userData = await authApi.getCurrentUser()
      user.value = userData

      // Get the redirect URL (either saved voting page or default dashboard)
      const redirectUrl = getPostLoginRedirectUrl()

      return {
        success: true,
        requiresPasswordChange: response.requires_password_change,
        redirectUrl,
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.',
      }
    } finally {
      isLoading.value = false
    }
  }

  const register = async (email: string, firstName: string, lastName: string) => {
    isLoading.value = true
    try {
      const registerData: RegisterRequest = { email, firstName, lastName }
      const response = await authApi.register(registerData)

      return { success: true, message: response.message }
    } catch (error: any) {
      console.error('Registration failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed. Please try again.',
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    clearAuth()
    // Redirect to login page
    window.location.href = '/login'
  }

  const clearAuth = () => {
    user.value = null
    token.value = null
    localStorage.removeItem('clipflow_token')
    localStorage.removeItem('clipflow_user')
  }

  const fetchCurrentUser = async () => {
    if (!token.value) return null

    isUserLoading.value = true
    try {
      const userData = await authApi.getCurrentUser()
      user.value = userData
      return userData
    } catch (error: any) {
      console.error('Failed to fetch user data:', error)
      // If token is invalid, clear auth
      if (error.response?.status === 401) {
        clearAuth()
      }
      return null
    } finally {
      isUserLoading.value = false
    }
  }

  const changePassword = async (newPassword: string) => {
    try {
      const changePasswordData: ChangePasswordRequest = { newPassword }
      const response = await authApi.changePassword(changePasswordData)
      return { success: true, message: response.message }
    } catch (error: any) {
      console.error('Password change failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Password change failed. Please try again.',
      }
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      const response = await authApi.forgotPassword({ email })
      return { success: true, message: response.message }
    } catch (error: any) {
      console.error('Forgot password failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send reset email. Please try again.',
      }
    }
  }

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      const resetPasswordData = { token, newPassword }
      const response = await authApi.resetPassword(resetPasswordData)
      return { success: true, message: response.message }
    } catch (error: any) {
      console.error('Password reset failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Password reset failed. Please try again.',
      }
    }
  }

  const verifyToken = async () => {
    try {
      await authApi.verifyToken()
      // If token is valid, fetch fresh user data
      await fetchCurrentUser()
      return true
    } catch (error) {
      console.error('Token verification failed:', error)
      clearAuth()
      return false
    }
  }

  // Initialize auth on store creation
  initAuth()

  return {
    user,
    token: authToken,
    isLoading,
    isUserLoading,
    isAuthenticated,
    currentUser,
    login,
    register,
    logout,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyToken,
    fetchCurrentUser,
    initAuth,
  }
})
